import React, { useState, useEffect } from 'react';
import ReviewStats from './ReviewStats';
import ReviewsList from './ReviewsList';
import ReviewForm from './ReviewForm';
import { Alert, AlertDescription } from './ui/alert';
import { Info } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProductReviews({ productId }) {
    const [stats, setStats] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [userOrders, setUserOrders] = useState([]);
    const [canReview, setCanReview] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchReviewStats = React.useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/reviews/product/${productId}/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching review stats:', error);
        }
    }, [productId]);

    const checkUserCanReview = React.useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            // Get current user
            const response = await axios.get(`${API_URL}/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentUser(response.data);

            // Get user's orders containing this product
            const ordersResponse = await axios.get(`${API_URL}/store-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Filter orders that contain this product
            // Note: This logic depends on the structure of store orders
            // Assuming store orders have items array
            const eligibleOrders = ordersResponse.data.filter(order => {
                const item = order.items?.find(i => i.product_id === productId);
                if (!item) return false;

                const validStatuses = ['DELIVERED', 'COMPLETED', 'delivered', 'completed'];
                // Check if the specific item is delivered or the order is completed
                const isItemCompleted = item.delivery_status && validStatuses.includes(item.delivery_status);
                const isOrderCompleted = validStatuses.includes(order.delivery_status); // Check order level status too if needed

                return isItemCompleted || isOrderCompleted;
            });

            setUserOrders(eligibleOrders);

            if (eligibleOrders.length > 0) {
                setCanReview(true);
                setSelectedOrderId(eligibleOrders[0].id); // Select first eligible order
            }
        } catch (error) {
            console.error('Error checking review eligibility:', error);
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        fetchReviewStats();
        checkUserCanReview();
    }, [fetchReviewStats, checkUserCanReview]);

    const handleReviewSubmitted = () => {
        fetchReviewStats();
        setCanReview(false); // Hide form after submission
    };

    const handleReviewDeleted = () => {
        fetchReviewStats();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Review Statistics */}
            {stats && stats.total_reviews > 0 && (
                <ReviewStats stats={stats} />
            )}

            {/* Review Form - Only show if user can review */}
            {canReview && selectedOrderId && (
                <div className="mb-8">
                    <ReviewForm
                        productId={productId}
                        orderId={selectedOrderId}
                        onReviewSubmitted={handleReviewSubmitted}
                    />
                </div>
            )}

            {/* Info message for non-eligible users */}
            {!canReview && currentUser && (
                <Alert className="border-blue-200 bg-blue-50">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                        Vous devez avoir acheté ce produit/service pour laisser un avis.
                    </AlertDescription>
                </Alert>
            )}

            {/* Reviews List */}
            <ReviewsList
                productId={productId}
                currentUserId={currentUser?.id}
                onReviewDeleted={handleReviewDeleted}
            />
        </div>
    );
}
